/**
 * AgencyShareLinkModal.jsx
 * ------------------------------------------------------------------
 * Modal launched from a client row's "Share" button. Lists active
 * share links + lets the agency create a new one with expiry preset
 * and approval scope toggle. Copy-to-clipboard for the just-created
 * link URL.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  X, Plus, Copy, Check, Trash2, Loader2, AlertTriangle, ExternalLink, Share2,
} from "lucide-react";

// Frontend origin — share URL is `<origin>/agency-share/<token>`.
// We deliberately don't use REACT_APP_BACKEND_URL because that points
// at the API base; the link should open the SPA route in a browser.
const FRONTEND_ORIGIN = window.location.origin;

export default function AgencyShareLinkModal({ open, onClose, client, userEmail }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [allowApproval, setAllowApproval] = useState(true);
  const [revealUrl, setRevealUrl] = useState(null);  // newly created link URL
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/agency/clients/${client.id}/share-links`, {
      headers: { "X-User-Email": userEmail },
    }).then(r => { if (!cancelled) setLinks(r.data?.links || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, client, userEmail]);

  const refresh = async () => {
    const r = await axios.get(`${API}/agency/clients/${client.id}/share-links`, {
      headers: { "X-User-Email": userEmail },
    });
    setLinks(r.data?.links || []);
  };

  const handleCreate = async () => {
    setCreating(true); setRevealUrl(null);
    try {
      const r = await axios.post(`${API}/agency/clients/${client.id}/share-link`,
        { expiry_days: expiryDays, allow_approval: allowApproval },
        { headers: { "X-User-Email": userEmail } });
      const url = `${FRONTEND_ORIGIN}/agency-share/${r.data.token}`;
      setRevealUrl(url);
      await refresh();
    } catch (e) {
      alert("Vytvorenie linku zlyhalo: " + (e?.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  };

  const handleRevoke = async (id) => {
    if (!confirm("Naozaj zrušiť tento share link? Klient stratí prístup okamžite.")) return;
    try {
      await axios.delete(`${API}/agency/share-links/${id}`, {
        headers: { "X-User-Email": userEmail },
      });
      await refresh();
    } catch {}
  };

  const handleCopy = () => {
    if (!revealUrl) return;
    navigator.clipboard.writeText(revealUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="agency-share-modal"
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-950 border border-purple-700/40 rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 bg-gray-950 border-b border-gray-800">
          <div className="flex items-center gap-2 min-w-0">
            <Share2 className="w-4 h-4 text-purple-400" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-purple-400">Share links</div>
              <div className="text-sm font-bold truncate">{client?.name}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white" data-testid="agency-share-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 py-5 space-y-5">
          {/* Just-created link reveal */}
          {revealUrl && (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-sm" data-testid="agency-share-reveal">
              <div className="text-xs text-emerald-300 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-400" />
                Skopíruj URL teraz — token sa už nedá načítať
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-black/60 border border-gray-800 text-emerald-300 font-mono text-xs break-all rounded-sm" data-testid="agency-share-reveal-url">
                  {revealUrl}
                </code>
                <button
                  onClick={handleCopy}
                  data-testid="agency-share-reveal-copy"
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-sm inline-flex items-center gap-1"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <a
                  href={revealUrl} target="_blank" rel="noreferrer"
                  data-testid="agency-share-reveal-open"
                  className="px-2 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}

          {/* Create new link */}
          <section className="space-y-3">
            <h3 className="text-sm font-bold">Vytvoriť nový share link</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="text-gray-400 block mb-1">Platnosť</span>
                <select
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(Number(e.target.value))}
                  data-testid="agency-share-expiry"
                  className="w-full px-2 py-1.5 bg-black/50 border border-gray-800 focus:border-purple-500 text-gray-200 rounded-sm"
                >
                  <option value={7}>7 dní</option>
                  <option value={30}>30 dní</option>
                  <option value={90}>90 dní</option>
                  <option value={0}>Bez expirácie</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer pt-5">
                <input
                  type="checkbox"
                  checked={allowApproval}
                  onChange={(e) => setAllowApproval(e.target.checked)}
                  data-testid="agency-share-allow-approval"
                  className="accent-purple-500"
                />
                Klient môže schvaľovať / zamietať GEO stránky
              </label>
            </div>
            <button
              onClick={handleCreate}
              disabled={creating}
              data-testid="agency-share-create-btn"
              className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Vytvoriť link
            </button>
          </section>

          {/* Active links list */}
          <section>
            <h3 className="text-sm font-bold mb-2">Aktívne linky ({links.length})</h3>
            {loading && <div className="text-gray-400 text-xs"><Loader2 className="w-3.5 h-3.5 animate-spin inline" /></div>}
            {!loading && links.length === 0 && !revealUrl && (
              <div className="text-gray-500 text-xs">Žiadne aktívne linky.</div>
            )}
            {links.length > 0 && (
              <div className="space-y-1">
                {links.map((l, i) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 p-2 bg-black/40 rounded-sm" data-testid={`agency-share-link-${i}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono text-purple-300">{l.token_preview}</div>
                      <div className="text-[10px] text-gray-500">
                        Vytvorený {new Date(l.created_at).toLocaleDateString("sk-SK")}
                        {l.expires_at && ` · platnosť do ${new Date(l.expires_at).toLocaleDateString("sk-SK")}`}
                        {!l.expires_at && " · bez expirácie"}
                        {" · "}{l.access_count || 0}× otvorený
                        {l.allow_approval && " · môže schvaľovať"}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(l.id)}
                      data-testid={`agency-share-revoke-${i}`}
                      className="px-2 py-1 bg-rose-900/40 hover:bg-rose-900 text-rose-200 text-xs rounded-sm"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
